import { useRef, useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { Upload, Image as ImageIcon, FileText, Loader2, Trash2, ExternalLink, Link as LinkIcon } from 'lucide-react';
import { uploadImage, uploadPdf, deleteRemoteFile } from '@/lib/uploader';
import { cn } from '@/lib/utils';

/**
 * MediaUploader — a reusable admin upload widget for images or PDFs.
 *
 * Props:
 * - value: string (current CDN url) or ''
 * - onChange: (url) => void
 * - kind: 'image' | 'pdf'
 * - prefix?: string (storage folder, defaults to kind-appropriate)
 * - testId?: string
 * - allowUrlPaste?: boolean (default true — user can also paste an external url)
 */
export const MediaUploader = ({
    value,
    onChange,
    kind = 'image',
    prefix,
    testId,
    allowUrlPaste = true,
    className = '',
}) => {
    const inputRef = useRef(null);
    const [uploading, setUploading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [dragOver, setDragOver] = useState(false);
    const [urlDraft, setUrlDraft] = useState('');

    useEffect(() => {
        setUrlDraft(value || '');
    }, [value]);

    const isImage = kind === 'image';
    const accept = isImage
        ? 'image/jpeg,image/png,image/webp,image/gif,image/svg+xml'
        : 'application/pdf';
    const doUpload = isImage ? uploadImage : uploadPdf;

    const handleFiles = async (files) => {
        if (!files || !files.length) return;
        const file = files[0];
        // client-side type check
        if (isImage && !file.type.startsWith('image/')) {
            toast.error('Please choose an image file');
            return;
        }
        if (!isImage && file.type !== 'application/pdf') {
            toast.error('Please choose a PDF file');
            return;
        }
        setUploading(true);
        setProgress(10);
        try {
            // fake progress ticker
            const t = setInterval(
                () => setProgress((p) => (p < 85 ? p + 8 : p)),
                180
            );
            const result = await doUpload(file, prefix);
            clearInterval(t);
            setProgress(100);
            onChange && onChange(result.url);
            toast.success('Uploaded');
            setTimeout(() => setProgress(0), 400);
        } catch (e) {
            const msg =
                e?.response?.data?.detail || e?.message || 'Upload failed';
            toast.error(msg);
            setProgress(0);
        } finally {
            setUploading(false);
        }
    };

    const onDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        handleFiles(e.dataTransfer?.files);
    };

    const onPickClick = () => inputRef.current?.click();

    const onClear = async () => {
        // Best-effort delete on CDN if value is a Bunny url; ignore failure.
        try {
            await deleteRemoteFile(value);
        } catch (_) {
            // silent — user might have entered an external URL
        }
        onChange && onChange('');
        toast.success('Removed');
    };

    const applyUrlDraft = () => {
        const trimmed = (urlDraft || '').trim();
        if (!trimmed) return;
        onChange && onChange(trimmed);
        toast.success('URL applied');
    };

    return (
        <div className={cn('space-y-3', className)} data-testid={testId}>
            {/* Preview */}
            {value && (
                <div
                    className="rounded-lg border border-border bg-[hsl(var(--secondary))]/40 overflow-hidden"
                    data-testid={testId ? `${testId}-preview` : undefined}
                >
                    <div className="flex items-center gap-3 p-3">
                        <div className="h-14 w-14 rounded-md bg-white border border-border overflow-hidden flex items-center justify-center shrink-0">
                            {isImage ? (
                                <img
                                    src={value}
                                    alt=""
                                    className="h-full w-full object-cover"
                                    onError={(e) => {
                                        e.currentTarget.style.display = 'none';
                                    }}
                                />
                            ) : (
                                <FileText className="h-6 w-6 text-[hsl(var(--primary))]" />
                            )}
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="text-xs text-muted-foreground truncate">
                                {value}
                            </div>
                        </div>
                        <a
                            href={value}
                            target="_blank"
                            rel="noreferrer noopener"
                            className="inline-flex items-center justify-center h-8 w-8 rounded-md hover:bg-[hsl(var(--secondary))]"
                            aria-label="Open"
                            data-testid={testId ? `${testId}-open` : undefined}
                        >
                            <ExternalLink className="h-4 w-4 text-muted-foreground" />
                        </a>
                        <Button
                            variant="ghost"
                            size="icon"
                            className="text-destructive hover:text-destructive"
                            onClick={onClear}
                            disabled={uploading}
                            data-testid={testId ? `${testId}-remove` : undefined}
                        >
                            <Trash2 className="h-4 w-4" />
                        </Button>
                    </div>
                </div>
            )}

            {/* Drop zone */}
            <div
                role="button"
                tabIndex={0}
                onClick={onPickClick}
                onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onPickClick()}
                onDragOver={(e) => {
                    e.preventDefault();
                    setDragOver(true);
                }}
                onDragLeave={() => setDragOver(false)}
                onDrop={onDrop}
                className={cn(
                    'flex flex-col items-center justify-center rounded-lg border-2 border-dashed p-6 cursor-pointer text-center transition-colors',
                    dragOver
                        ? 'border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/5'
                        : 'border-border bg-[hsl(var(--secondary))]/30 hover:bg-[hsl(var(--secondary))]/60'
                )}
                data-testid={testId ? `${testId}-dropzone` : undefined}
            >
                <div className="h-10 w-10 rounded-full bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))] flex items-center justify-center mb-2">
                    {uploading ? (
                        <Loader2 className="h-5 w-5 animate-spin" />
                    ) : isImage ? (
                        <ImageIcon className="h-5 w-5" />
                    ) : (
                        <FileText className="h-5 w-5" />
                    )}
                </div>
                <div className="text-sm font-medium text-foreground">
                    {uploading
                        ? 'Uploading…'
                        : value
                        ? isImage
                            ? 'Replace image'
                            : 'Replace PDF'
                        : isImage
                        ? 'Click or drop an image here'
                        : 'Click or drop a PDF here'}
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                    {isImage
                        ? 'JPG, PNG, WebP, GIF or SVG · up to 8 MB'
                        : 'PDF only · up to 25 MB'}
                </div>
                <input
                    ref={inputRef}
                    type="file"
                    accept={accept}
                    className="hidden"
                    onChange={(e) => handleFiles(e.target.files)}
                />
                {progress > 0 && (
                    <div className="w-full max-w-xs mt-3">
                        <Progress value={progress} className="h-1.5" />
                    </div>
                )}
            </div>

            {allowUrlPaste && (
                <details className="group">
                    <summary className="cursor-pointer text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
                        <LinkIcon className="h-3 w-3" />
                        Or paste an external URL
                    </summary>
                    <div className="mt-2 flex gap-2">
                        <Input
                            value={urlDraft}
                            onChange={(e) => setUrlDraft(e.target.value)}
                            placeholder={
                                isImage
                                    ? 'https://example.com/image.jpg'
                                    : 'https://example.com/file.pdf'
                            }
                            data-testid={testId ? `${testId}-url-input` : undefined}
                        />
                        <Button
                            variant="outline"
                            onClick={applyUrlDraft}
                            data-testid={testId ? `${testId}-url-apply` : undefined}
                        >
                            Apply
                        </Button>
                    </div>
                </details>
            )}
        </div>
    );
};

export default MediaUploader;
